import {
    type AddLineOutcome,
    addBrandAlias,
    addBrandLine,
    createBrandFromName,
} from '@/app/brands/brandCatalogActions';
import logger from '@/util/logger';
import { BrandRequestDao } from '@rock/db-admin';
import {
    type Brand,
    type BrandLine,
    type BrandRequest,
    isBrandRequestOpen,
    type Timestamp,
    type UserId,
} from '@rock/types';

const log = logger('brandRequestActions');
const brandRequestDao = new BrandRequestDao();

/** Why a resolution could not be applied — shown as the toast, so each reads as a sentence. */
export class BrandRequestResolutionError extends Error {}

/**
 * The human decisions on an open brand request. The catalog writes ride the same helpers the Brands
 * pages use (`createBrandFromName`, `addBrandAlias`), which also flip the request — the fan-out reads the
 * brand the resolution names, so the catalog write always lands first. Each returns the brand the name
 * now resolves to, for navigation.
 */
export async function resolveAsNewBrand(request: BrandRequest, by: UserId): Promise<Brand> {
    return createBrandNamed(request.brand, by);
}

/** The catalog brand under `name`: created (reading as `displayName`, when given), or the one the name already resolves to. */
export async function createBrandNamed(name: string, by: UserId, displayName?: string): Promise<Brand> {
    const outcome = await createBrandFromName(name, by, displayName);
    if (outcome.status === 'refused') throw new BrandRequestResolutionError(outcome.reason);
    return outcome.brand;
}

export async function resolveAsAlias(request: BrandRequest, brand: Brand, by: UserId): Promise<Brand> {
    const outcome = await addBrandAlias(brand, request.brand, by);
    if (outcome.status === 'refused') throw new BrandRequestResolutionError(outcome.reason);
    if (outcome.status === 'taken') {
        throw new BrandRequestResolutionError(
            `“${request.brand}” already resolves to ${outcome.brand.canonicalName}; merge the two brands first.`,
        );
    }
    return brand;
}

/**
 * The request's name becomes a line of `brand`: the line's keys plus the request's own spelling resolve to
 * it. Onto a line the brand already has, a spelling that can't be a key (a bare "Brunswick") mints nothing
 * and the request is settled to the line all the same.
 */
export async function resolveAsLine(request: BrandRequest, brand: Brand, lineName: string, by: UserId): Promise<Brand> {
    const outcome = await addBrandLine(brand, { displayName: lineName, extraNames: [request.brand] }, by);
    const line = requireLine(outcome);
    const now = new Date().toISOString() as Timestamp;
    const fresh = await brandRequestDao.getBrandRequest(request.id);
    if (fresh && isBrandRequestOpen(fresh)) {
        await brandRequestDao.resolveBrandRequest(
            request.id,
            { kind: 'line', brandId: brand.id, lineId: line.id, by, at: now },
            now,
        );
        log.info('Resolved brand request as a line', { requestId: request.id, brandId: brand.id, lineId: line.id });
    }
    return brand;
}

export async function resolveAsNotABrand(request: BrandRequest, reason: string, by: UserId): Promise<void> {
    const now = new Date().toISOString() as Timestamp;
    await brandRequestDao.resolveBrandRequest(request.id, { kind: 'not-a-brand', reason, by, at: now }, now);
    log.info('Resolved request as not a brand', { requestId: request.id });
}

/** Sets a pending name aside until it is seen again. */
export async function parkRequest(request: BrandRequest, by: UserId): Promise<void> {
    const parked = await brandRequestDao.parkBrandRequest(request.id, by, new Date().toISOString() as Timestamp);
    if (!parked) throw changedMeanwhile(request);
}

/** Back to the queue by hand. */
export async function unparkRequest(request: BrandRequest): Promise<void> {
    const unparked = await brandRequestDao.unparkBrandRequest(request.id, new Date().toISOString() as Timestamp);
    if (!unparked) throw changedMeanwhile(request);
}

function changedMeanwhile(request: BrandRequest): BrandRequestResolutionError {
    return new BrandRequestResolutionError(`“${request.brand}” changed while this page was open. Nothing was saved.`);
}

/** The line a request settles onto: the one just added, or the one the brand already has. */
function requireLine(outcome: AddLineOutcome): BrandLine {
    if (outcome.status === 'added') return outcome.line;
    if (outcome.existingLine) return outcome.existingLine;
    throw new BrandRequestResolutionError(outcome.reason);
}
